import {
  BoxGeometry,
  Color,
  CylinderGeometry,
  Float32BufferAttribute,
  Group,
  Matrix4,
  Mesh,
  MeshStandardMaterial,
  Quaternion,
  SphereGeometry,
  Vector3,
} from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { clamp, damp, dampAngle } from '../../../utils/math.js';
import { createRandom } from '../../../utils/random.js';
import { GROUP, MASK } from '../../../physics/groups.js';
import { rideDoorPoint, rideDoorSide, rideSeats } from './rides.js';

// Shared, never-mutated values used when parking / repositioning person colliders.
const QUAT_IDENTITY = new Quaternion();
// When a hitchhiker is attached to a vehicle (boarding / riding / dropping) its
// collider is parked far below the world so it cannot interfere with the truck.
const COLLIDER_PARKED = new Vector3(0, -1000, 0);
// Half the standing figure's height; the capsule centre sits this far above the
// group origin (the feet) so the collider wraps the body.
const COLLIDER_CENTER = 0.78;
// How far off the road a dropped-off person walks to reach the kerb.
const KERB_DISTANCE = 3.5;

/**
 * Roadside hitchhikers for Street 60.
 *
 * People are built from vertex-coloured primitives and animated by hand: they
 * stand at the kerb, raise an arm when the Hilux gets close, jog to the tailgate
 * when it stops, and climb into the bed. Once they have ridden far enough, they
 * hop out at the next stop. The manager owns all of that little state machine;
 * the Game owns the money and only receives pickup/drop-off events.
 */

const SIDEWALK_Y = 0.055;
const SIDEWALK_LATERAL = 12.0;
const BED_FLOOR_Y = 0.292;

/** Riders perch on the rim of the cargo bed: two along the side rails and one on
 *  the tailgate edge, rather than sitting in the middle of the floor. */
const BED_SLOTS = [
  new Vector3(0.72, BED_FLOOR_Y, -1.35),
  new Vector3(-0.72, BED_FLOOR_Y, -1.35),
  new Vector3(0, BED_FLOOR_Y, -2.18),
];

const DEFAULT_CONFIG = {
  money: 50,
  capacity: BED_SLOTS.length,
  waveRadius: 32,
  pickupRadius: 8,
  stopSpeed: 1.2,
  pickupDelay: 0.3,
  minRideDistance: 100,
  dropDelay: 0.75,
  approachSpeed: 5.0,
  abortSpeed: 2.2,
};

const RAKSHA_DEFAULTS = {
  passengerCapacity: 2,
  pickupRadius: 8.5,
  pickupStopTime: 3.2,
  minRideDistance: 180,
  dropDelay: 1.2,
  respawnDelay: 24,
};

/**
 * Places along the route (metres from the south end). East-side sites face
 * northbound traffic; west-side sites serve southbound drivers and give the
 * rakshas reachable fares on their station return loops. The sites deliberately
 * avoid tea stands, station driveways and most junctions.
 */
const SITES = [
  { s: 940, side: 1, look: 0, kind: 'man', scale: 1.02 },
  { s: 1120, side: -1, look: 4, kind: 'woman', scale: 0.98 },
  { s: 1240, side: 1, look: 1, kind: 'youth', scale: 0.96 },
  { s: 1500, side: -1, look: 7, kind: 'elderMan', scale: 1.01 },
  { s: 1710, side: -1, look: 2, kind: 'woman', scale: 1.04 }, // beside the west-side bus shelter
  { s: 2010, side: 1, look: 3, kind: 'youth', scale: 0.94 },
  { s: 2180, side: -1, look: 8, kind: 'man', scale: 0.97 },
  { s: 2460, side: -1, look: 5, kind: 'elderWoman', scale: 1.02 },
  { s: 2730, side: 1, look: 6, kind: 'woman', scale: 1.01 },
  { s: 3050, side: -1, look: 9, kind: 'youth', scale: 0.99 },
  { s: 3580, side: 1, look: 0, kind: 'man', scale: 1.03 },
  { s: 3980, side: -1, look: 7, kind: 'woman', scale: 0.97 },
  { s: 4380, side: 1, look: 2, kind: 'elderMan', scale: 1.05 },
  { s: 4950, side: 1, look: 9, kind: 'youth', scale: 0.95 },
];

/**
 * Body types for the people along the kerb. Each one changes the silhouette
 * (build and height), the clothing cut, and the head covering, so the route is
 * no longer a row of identical galabeya-clad men — there are women in abayas
 * with headscarves, teenage boys in t-shirts and trousers, and older folk with
 * a grey cap and a slight stoop.
 */
const KINDS = {
  // Adult man in a long galabeya with a skullcap.
  man:   { clothing: 'robe',  headCover: 'hat',      bScale: 1.0,  hScale: 1.0,  torsoR: 0.2,  stoop: 0 },
  // Woman in an abaya with a headscarf (hijab); a touch shorter and slimmer.
  woman: { clothing: 'abaya', headCover: 'hijab',    bScale: 0.9,  hScale: 0.95, torsoR: 0.18, stoop: 0 },
  // Teenage boy in a t-shirt and trousers, much shorter and narrower.
  youth: { clothing: 'youth', headCover: 'hair',     bScale: 0.78, hScale: 0.8,  torsoR: 0.16, stoop: 0 },
  // Older man: a galabeya with a grey cap and a slight stoop.
  elderMan: { clothing: 'robe',  headCover: 'hat-grey', bScale: 0.95, hScale: 0.97, torsoR: 0.19, stoop: 0.09 },
  // Older woman: an abaya with a grey headscarf and a slight stoop.
  elderWoman: { clothing: 'abaya', headCover: 'hijab', bScale: 0.9, hScale: 0.94, torsoR: 0.18, stoop: 0.08 },
};

/** Skin tones and hair/scarf colours so even two people of the same kind read
 *  as different individuals. */
const SKIN_TONES = [0x6a4b36, 0x543827, 0x7d5638, 0x4a3322];
const HAIR_TONES = [0x1c140e, 0x2b1d12, 0x3a2a1a];
const HIJAB_TONES = [0x39495a, 0x5a3a4a, 0x2f3a2f, 0x4a3a5a, 0x6a5030, 0x46343a];
const YOUTH_SHIRTS = [0x4a7fb5, 0xc24b4b, 0x4f9d6b, 0xd9a441, 0x7a5bb0, 0x3a8fa0, 0xc96a3a, 0x6b8f3a, 0xb04f7a, 0x4a6fb5];
const TROUSERS = 0x33312c;

/** Pale galabeyas, tobes and wraps in the same low-poly language as the city. */
const LOOKS = [
  { robe: 0xe8dfcb, wrap: 0xe2d6c2, bag: 0x6c5137 },
  { robe: 0xb8cbd2, wrap: 0xd8cebb, bag: null },
  { robe: 0xc97858, wrap: 0xe6d9c3, bag: 0x4d3e2f },
  { robe: 0xa9b89a, wrap: 0xded4c0, bag: 0x694e35 },
  { robe: 0x92a7b8, wrap: 0xd1c8b5, bag: null },
  { robe: 0xd9b66d, wrap: 0x4e5e54, bag: 0x3c2f24 },
  { robe: 0xc6b8d3, wrap: 0xe7ddca, bag: 0x4c3b29 },
  { robe: 0xe0d2b5, wrap: 0x7f9a8e, bag: 0x785b3d },
  { robe: 0xa7bab7, wrap: 0xe3d8c3, bag: null },
  { robe: 0xd49b74, wrap: 0xd9cfbb, bag: 0x3c5e58 },
];

const SKIN_DARK = 0x543827;
const SHOE = 0x403126;

export function buildHitchhikers(level, route) {
  return new HitchhikerManager({
    level,
    route,
    config: {
      ...DEFAULT_CONFIG,
      ...(level.config?.hitchhikers ?? {}),
    },
  });
}

class HitchhikerManager {
  constructor({ level, route, config }) {
    this.level = level;
    this.route = route;
    this.config = config;
    this.rakshaConfig = {
      ...RAKSHA_DEFAULTS,
      ...(level.config?.rakshas ?? {}),
    };
    this.people = [];
    this.status = '';
    this.statusMode = 'hail';
    this.onPickup = null;
    this.onDropoff = null;

    this.time = 0;
    this.stoppedTime = 0;
    this.speed = 0;
    this._lastTruckPosition = new Vector3();
    this._hasLastTruckPosition = false;
    this._forward = new Vector3();
    this._right = new Vector3();
    this._boardPoint = new Vector3();
    this._rakshaPoint = new Vector3();
    this._rakshaTarget = new Vector3();
    this._colliderPos = new Vector3();
    this._collidersReady = false;

    const material = new MeshStandardMaterial({
      vertexColors: true,
      roughness: 0.93,
      metalness: 0.02,
    });
    const random = createRandom(90317);

    SITES.forEach((site, index) => {
      const look = LOOKS[site.look % LOOKS.length];
      const k = KINDS[site.kind] ?? KINDS.man;
      const figure = createFigure(look, k, material, index);
      const frame = route.offsetFrame(site.s, site.side * SIDEWALK_LATERAL);
      const toRoad = frame.right.clone().multiplyScalar(-site.side);
      const homeYaw = Math.atan2(toRoad.x, toRoad.z);
      const group = figure.group;

      group.name = `hitchhiker-${index + 1}`;
      // Non-uniform so build and height vary with the body type, not just hue.
      const buildScale = k.bScale * (site.scale ?? 1);
      const heightScale = k.hScale * (site.scale ?? 1);
      group.scale.set(buildScale, heightScale, buildScale);
      group.position.set(frame.position.x, SIDEWALK_Y, frame.position.z);
      group.rotation.y = homeYaw;

      const person = {
        ...figure,
        index,
        kind: site.kind ?? 'man',
        scale: site.scale ?? 1,
        restScale: group.scale.clone(),
        // Kinematic physics collider so the player car cannot drive through the
        // person. Built lazily once the physics world is ready.
        collider: null,
        _colliderOn: false,
        colliderRadius: 0.34 * buildScale,
        colliderCenter: COLLIDER_CENTER * heightScale,
        home: group.position.clone(),
        homeYaw,
        state: 'waiting',
        slot: -1,
        rideDistance: 0,
        waving: false,
        truckDistance: Infinity,
        rakshaDistance: Infinity,
        hailRaksha: null,
        raksha: null,
        deliveredBy: null,
        respawnTimer: 0,
        respawnDelay: RAKSHA_DEFAULTS.respawnDelay,
        walkPhase: random() * Math.PI * 2,
        wavePhase: random() * Math.PI * 2,
        climbTime: 0,
        climbYaw: homeYaw,
        climbStart: new Vector3(),
        climbTarget: new Vector3(),
        dropTime: 0,
        dropStart: new Vector3(),
        dropTarget: new Vector3(),
        leaveTarget: new Vector3(),
        faceYaw: homeYaw,
      };

      this.people.push(person);
      level.root.add(group);
    });
  }

  /**
   * Called from the level's render update after the vehicle pose is set. The
   * timestep is visual, which keeps the people moving even when the fixed
   * physics tick is skipped during an uncapped render.
   */
  update(dt, player) {
    if (!player?.mesh) {
      this.status = '';
      return;
    }

    const step = clamp(dt, 0, 0.1);
    this.time += step;
    this.vehicle = player;

    const truck = player.mesh.position;
    const speed = Math.abs(player.speed ?? player.focus?.speed ?? 0);
    this.speed = speed;
    const stopped = speed < this.config.stopSpeed;
    this.stoppedTime = stopped ? this.stoppedTime + step : 0;

    // The people use local-to-world positions for the tailgate and the bed.
    // Keep the truck matrix current when rendering is uncapped; the renderer
    // updates the rest of the truck's children later in the frame.
    player.mesh.updateWorldMatrix(true, false);

    if (this._hasLastTruckPosition) {
      const moved = Math.hypot(
        truck.x - this._lastTruckPosition.x,
        truck.z - this._lastTruckPosition.z,
      );
      // A respawn or teleport should not count as hundreds of metres of fare.
      if (moved < 5) {
        for (const person of this.people) {
          if (person.state === 'riding') person.rideDistance += moved;
        }
      }
    }
    this._lastTruckPosition.copy(truck);
    this._hasLastTruckPosition = true;

    for (const person of this.people) {
      this.#updatePerson(person, step);
    }

    if (this.stoppedTime >= this.config.pickupDelay) this.#tryPickup();
    if (this.stoppedTime >= this.config.dropDelay) this.#tryDropOff();

    for (const person of this.people) {
      this.#animateArms(person, step);
    }

    // Keep each hitchhiker's physics collider glued to its visual position so
    // the player car is stopped when it drives into one.
    this.#ensureColliders();
    for (const person of this.people) {
      this.#updateCollider(person);
    }

    this.#updateStatus();
  }

  /** Put everybody back on the kerb and clear a run. */
  reset(player = this.vehicle) {
    this.vehicle = player;
    this.time = 0;
    this.stoppedTime = 0;
    this.speed = 0;
    this.status = '';
    this.statusMode = 'hail';

    for (const person of this.people) {
      if (person.raksha?.raksha) {
        const ride = person.raksha.raksha;
        ride.passengers = ride.passengers.filter((rider) => rider !== person);
        ride.stop = null;
        ride.target = null;
        ride.cooldown = 4;
      }
      person.raksha = null;
      if (person.group.parent !== this.level.root) {
        // `attach` keeps the world transform while moving the group back into
        // the level; the home position below replaces it immediately.
        this.level.root.attach(person.group);
      }

      person.group.visible = true;
      person.group.position.copy(person.home);
      person.group.rotation.set(0, person.homeYaw, 0);
      person.group.scale.copy(person.restScale);
      person.body.geometry = person.standingGeometry;
      person.body.rotation.set(0, 0, 0);
      person.state = 'waiting';
      person.slot = -1;
      person.rideDistance = 0;
      person.waving = false;
      person.truckDistance = Infinity;
      person.rakshaDistance = Infinity;
      person.hailRaksha = null;
      person.deliveredBy = null;
      person.respawnTimer = 0;
      person.walkPhase = 0;
      person.climbTime = 0;
      person.boardLeg = 0;
      person.dropTime = 0;
      person.leftArm.rotation.set(0, 0, -0.08);
      person.rightArm.rotation.set(0, 0, 0.08);
    }

    if (player?.mesh) {
      this._lastTruckPosition.copy(player.mesh.position);
      this._hasLastTruckPosition = true;
    } else {
      this._hasLastTruckPosition = false;
    }
  }

  #updatePerson(person, dt) {
    switch (person.state) {
      case 'waiting':
        this.#updateWaiting(person, dt);
        break;
      case 'boarding':
      case 'climbing':
        this.#updateBoarding(person, dt);
        break;
      case 'returning':
        this.#updateReturning(person, dt);
        break;
      case 'riding':
        // The group is a child of the truck; its local slot handles the rest.
        break;
      case 'dropping':
        this.#updateDropping(person, dt);
        break;
      case 'leaving':
        this.#updateLeaving(person, dt);
        break;
      case 'rakshaBoarding':
      case 'rakshaClimbing':
        this.#updateRakshaBoarding(person, dt);
        break;
      case 'rakshaRiding':
        // Attached to the taxi's rear bench; distance is tracked by trafficFlow.
        break;
      case 'rakshaDropping':
        this.#updateRakshaDropping(person, dt);
        break;
      case 'rakshaLeaving':
        this.#updateRakshaLeaving(person, dt);
        break;
      case 'delivered':
        this.#updateDelivered(person, dt);
        break;
      default:
        break;
    }
  }

  #updateWaiting(person, dt) {
    const truck = this.vehicle.mesh.position;
    const dx = truck.x - person.group.position.x;
    const dz = truck.z - person.group.position.z;
    person.truckDistance = Math.hypot(dx, dz);
    const hailRaksha = person.hailRaksha;
    const rakshaIsCloser = hailRaksha && person.rakshaDistance < person.truckDistance;
    person.waving = Math.min(person.truckDistance, person.rakshaDistance) <= this.config.waveRadius;

    const targetYaw = rakshaIsCloser
      ? Math.atan2(
        hailRaksha.x - person.group.position.x,
        hailRaksha.z - person.group.position.z,
      )
      : person.waving
        ? Math.atan2(dx, dz)
        : person.homeYaw;
    person.group.rotation.y = dampAngle(
      person.group.rotation.y,
      targetYaw,
      7,
      dt,
    );

    const bob = person.waving
      ? Math.abs(Math.sin(this.time * 4.6 + person.wavePhase)) * 0.022
      : 0;
    person.group.position.y = SIDEWALK_Y + bob;
  }

  #updateBoarding(person, dt) {
    const speed = Math.abs(this.vehicle.speed ?? 0);
    if (speed > this.config.abortSpeed) {
      // The driver changed their mind and pulled away. Walk back to the kerb.
      person.state = 'returning';
      person.slot = -1;
      return;
    }

    if (person.state === 'boarding') {
      // Leg 0: walk around the back of the truck on the hitchhiker's side,
      // staying clear of the body. Leg 1: step in to the tailgate.
      const target =
        person.boardLeg === 0 ? this.#rearSidePoint(person) : this.#boardPoint();
      const dx = target.x - person.group.position.x;
      const dz = target.z - person.group.position.z;
      const distance = Math.hypot(dx, dz);

      if (distance > 0.45) {
        const step = Math.min(distance, this.config.approachSpeed * dt);
        person.group.position.x += (dx / distance) * step;
        person.group.position.z += (dz / distance) * step;
        person.group.position.y =
          SIDEWALK_Y +
          Math.abs(Math.sin(person.walkPhase)) * 0.035;
        person.walkPhase += dt * 9;
        person.group.rotation.y = dampAngle(
          person.group.rotation.y,
          Math.atan2(dx, dz),
          13,
          dt,
        );
        return;
      }

      if (person.boardLeg === 0) {
        // Reached the rear, alongside the body. Now move in to the tailgate.
        person.boardLeg = 1;
        return;
      }

      // At the tailgate: climb over it into the assigned bed slot.
      person.state = 'climbing';
      person.climbTime = 0;
      person.climbStart.copy(person.group.position);
      this.#bedWorld(person.slot, person.climbTarget);
      this.#truckForward(this._forward);
      person.climbYaw = Math.atan2(this._forward.x, this._forward.z);
      return;
    }

    person.climbTime += dt / 0.42;
    const t = clamp(person.climbTime, 0, 1);
    const eased = t * t * (3 - 2 * t);
    person.group.position.lerpVectors(
      person.climbStart,
      person.climbTarget,
      eased,
    );
    person.group.position.y += Math.sin(Math.PI * t) * 0.58;
    person.group.rotation.y = dampAngle(
      person.group.rotation.y,
      person.climbYaw,
      12,
      dt,
    );

    if (t >= 1) this.#attachToBed(person);
  }

  #updateReturning(person, dt) {
    const dx = person.home.x - person.group.position.x;
    const dz = person.home.z - person.group.position.z;
    const distance = Math.hypot(dx, dz);

    if (distance < 0.12) {
      person.state = 'waiting';
      person.group.position.copy(person.home);
      person.group.rotation.y = person.homeYaw;
      return;
    }

    const step = Math.min(distance, this.config.approachSpeed * 0.7 * dt);
    person.group.position.x += (dx / distance) * step;
    person.group.position.z += (dz / distance) * step;
    person.group.position.y =
      SIDEWALK_Y + Math.abs(Math.sin(person.walkPhase)) * 0.03;
    person.walkPhase += dt * 7.5;
    person.group.rotation.y = dampAngle(
      person.group.rotation.y,
      Math.atan2(dx, dz),
      12,
      dt,
    );
  }

  #updateDropping(person, dt) {
    person.dropTime += dt / 0.55;
    const t = clamp(person.dropTime, 0, 1);
    const eased = t * t * (3 - 2 * t);
    person.group.position.lerpVectors(
      person.dropStart,
      person.dropTarget,
      eased,
    );
    person.group.position.y += Math.sin(Math.PI * t) * 0.42;
    person.group.rotation.y = dampAngle(
      person.group.rotation.y,
      person.faceYaw,
      12,
      dt,
    );

    if (t >= 1) {
      person.group.position.copy(person.dropTarget);
      person.state = 'leaving';
      person.walkPhase = 0;
    }
  }

  #updateLeaving(person, dt) {
    const dx = person.leaveTarget.x - person.group.position.x;
    const dz = person.leaveTarget.z - person.group.position.z;
    const distance = Math.hypot(dx, dz);

    if (distance < 0.15) {
      person.group.position.copy(person.leaveTarget);
      person.state = 'delivered';
      return;
    }

    const step = Math.min(distance, 2.1 * dt);
    person.group.position.x += (dx / distance) * step;
    person.group.position.z += (dz / distance) * step;
    person.group.position.y =
      SIDEWALK_Y + Math.abs(Math.sin(person.walkPhase)) * 0.03;
    person.walkPhase += dt * 8.5;
    person.group.rotation.y = dampAngle(
      person.group.rotation.y,
      Math.atan2(dx, dz),
      12,
      dt,
    );
  }

  #animateArms(person, dt) {
    let leftZ = -0.08;
    let rightZ = 0.08;
    let leftX = 0;
    let rightX = 0;

    if (person.state === 'waiting') {
      if (person.waving) {
        rightZ = 2.24 + Math.sin(this.time * 9 + person.wavePhase) * 0.2;
        rightX = -0.16 + Math.sin(this.time * 4.5 + person.wavePhase) * 0.05;
      } else {
        leftZ = -0.08 - Math.sin(this.time * 1.4 + person.wavePhase) * 0.025;
        rightZ = 0.08 + Math.sin(this.time * 1.6 + person.wavePhase) * 0.025;
      }
    } else if (
      person.state === 'boarding' ||
      person.state === 'returning' ||
      person.state === 'leaving' ||
      person.state === 'rakshaBoarding' ||
      person.state === 'rakshaLeaving'
    ) {
      const swing = Math.sin(person.walkPhase) * 0.55;
      leftX = swing;
      rightX = -swing;
      leftZ = -0.06;
      rightZ = 0.06;
    } else if (
      person.state === 'climbing' ||
      person.state === 'dropping' ||
      person.state === 'rakshaClimbing' ||
      person.state === 'rakshaDropping'
    ) {
      leftZ = -0.65;
      rightZ = 0.65;
      leftX = -0.75;
      rightX = -0.75;
    } else if (person.state === 'riding' || person.state === 'rakshaRiding') {
      // Brace against the bed, with the arms a little out from the body.
      leftZ = -0.3;
      rightZ = 0.3;
      leftX = 0.2;
      rightX = 0.2;
    }

    person.leftArm.rotation.z = damp(
      person.leftArm.rotation.z,
      leftZ,
      13,
      dt,
    );
    person.rightArm.rotation.z = damp(
      person.rightArm.rotation.z,
      rightZ,
      13,
      dt,
    );
    person.leftArm.rotation.x = damp(
      person.leftArm.rotation.x,
      leftX,
      13,
      dt,
    );
    person.rightArm.rotation.x = damp(
      person.rightArm.rotation.x,
      rightX,
      13,
      dt,
    );
  }

  /** Clear the nearest-taxi hail links before the traffic update begins. */
  beginRideUpdate() {
    for (const person of this.people) {
      person.rakshaDistance = Infinity;
      person.hailRaksha = null;
    }
  }

  /**
   * Let a fare-paying vehicle stop for a passenger. Queue, entry and pump
   * states never call this hook, so passenger work cannot make a raksha cut the
   * fuel line. A bus calls it from its own circuit update instead.
   *
   * Everything vehicle-specific — how many seats, where the door is, which side
   * it opens onto — is read from the ride tables, so a six-seat minibus and a
   * two-seat raksha share this one state machine.
   */
  beforeRideMove(car, dt) {
    const settings = car.rakshaConfig;
    const seats = rideSeats(car);

    for (const person of this.people) {
      if (person.state !== 'waiting') continue;
      const distance = Math.hypot(car.x - person.group.position.x, car.z - person.group.position.z);
      if (distance < person.rakshaDistance && distance <= this.config.waveRadius) {
        person.rakshaDistance = distance;
        person.hailRaksha = car;
      }
    }

    const ride = car.raksha;
    ride.cooldown = Math.max(0, ride.cooldown - dt);
    if (car.state !== 'returning') return null;

    if (ride.stop) {
      if (car.exitSpeed < 0.4) ride.stopTimer -= dt;
      if (ride.stop === 'pickup') {
        if (!ride.target || !['rakshaBoarding', 'rakshaClimbing', 'rakshaRiding'].includes(ride.target.state)) {
          ride.stop = null;
          ride.target = null;
          ride.cooldown = 3;
        } else if (ride.stopTimer <= 0 && ride.target.state === 'rakshaRiding') {
          ride.stop = null;
          ride.target = null;
          ride.cooldown = 6;
        }
      } else if (ride.stop === 'dropoff') {
        if (!ride.dropStarted && car.exitSpeed < 0.4) {
          this.#startRakshaDropOff(car);
          ride.dropStarted = true;
        }
        if (ride.dropStarted && ride.stopTimer <= 0) {
          ride.stop = null;
          ride.dropStarted = false;
          ride.cooldown = 5;
        }
      }
      return { hold: Boolean(ride.stop), speedScale: 0 };
    }

    const distanceLeft = car.exitLength - car.exitDistance;
    const hasEligiblePassenger = ride.passengers.some(
      (person) => person.state === 'rakshaRiding' &&
        (person.rideDistance >= settings.minRideDistance || distanceLeft < 35),
    );
    if (hasEligiblePassenger) {
      ride.stop = 'dropoff';
      ride.stopTimer = settings.dropDelay;
      ride.dropStarted = false;
      return { hold: true, speedScale: 0 };
    }

    const capacity = Math.min(settings.passengerCapacity, seats.length);
    if (ride.passengers.length < capacity && ride.cooldown <= 0 && distanceLeft > settings.minRideDistance + 60) {
      let nearest = null;
      let nearestDistance = this.config.waveRadius;
      for (const person of this.people) {
        if (person.state !== 'waiting') continue;
        // A minibus only opens its door on the kerb it is driving along; a
        // raksha takes a fare from either side.
        if (!this.#atDoor(car, person)) continue;
        const distance = Math.hypot(car.x - person.group.position.x, car.z - person.group.position.z);
        if (distance < nearestDistance) {
          nearest = person;
          nearestDistance = distance;
        }
      }
      if (nearest && nearestDistance <= settings.pickupRadius) {
        const slot = seats.findIndex((_, index) =>
          !ride.passengers.some((person) => person.slot === index));
        if (slot >= 0) {
          nearest.state = 'rakshaBoarding';
          nearest.raksha = car;
          nearest.slot = slot;
          nearest.rideDistance = 0;
          nearest.waving = false;
          nearest.climbTime = 0;
          ride.passengers.push(nearest);
          ride.target = nearest;
          ride.stop = 'pickup';
          ride.stopTimer = settings.pickupStopTime;
          return { hold: true, speedScale: 0 };
        }
      }
      if (nearest && nearestDistance < 24) return { hold: false, speedScale: 0.48 };
    }
    return null;
  }

  /** True when `person` stands on the side of `car` that its door opens onto. */
  #atDoor(car, person) {
    const side = rideDoorSide(car);
    if (!side) return true;
    const dx = person.group.position.x - car.x;
    const dz = person.group.position.z - car.z;
    // Local +X in world space for a yaw rotation about Y.
    return (dx * Math.cos(car.yaw) - dz * Math.sin(car.yaw)) * side > 0.4;
  }

  afterRideMove(car, moved) {
    if (moved <= 0 || moved > 5) return;
    for (const person of car.raksha.passengers) {
      if (person.state === 'rakshaRiding') person.rideDistance += moved;
    }
  }

  #updateRakshaBoarding(person, dt) {
    const car = person.raksha;
    if (!car?.visual) {
      person.state = 'returning';
      person.slot = -1;
      return;
    }
    car.visual.updateWorldMatrix(true, false);

    if (person.state === 'rakshaBoarding') {
      const door = rideDoorPoint(car);
      const target = this._rakshaPoint.set(door.x, 0, door.z);
      car.visual.localToWorld(target);
      target.y = SIDEWALK_Y;
      const dx = target.x - person.group.position.x;
      const dz = target.z - person.group.position.z;
      const distance = Math.hypot(dx, dz);
      if (distance > 0.42) {
        const step = Math.min(distance, this.config.approachSpeed * dt);
        person.group.position.x += (dx / distance) * step;
        person.group.position.z += (dz / distance) * step;
        person.group.position.y = SIDEWALK_Y + Math.abs(Math.sin(person.walkPhase)) * 0.035;
        person.walkPhase += dt * 9;
        person.group.rotation.y = dampAngle(person.group.rotation.y, Math.atan2(dx, dz), 13, dt);
        return;
      }
      person.state = 'rakshaClimbing';
      person.climbTime = 0;
      person.climbStart.copy(person.group.position);
      person.faceYaw = car.yaw + Math.PI;
      return;
    }

    person.climbTime += dt / 0.42;
    const t = clamp(person.climbTime, 0, 1);
    const eased = t * t * (3 - 2 * t);
    const seats = rideSeats(car);
    const seat = seats[person.slot] ?? seats[0];
    const target = this._rakshaTarget.set(seat.x, seat.y, seat.z);
    car.visual.localToWorld(target);
    person.group.position.lerpVectors(person.climbStart, target, eased);
    person.group.position.y += Math.sin(Math.PI * t) * 0.35;
    person.group.rotation.y = dampAngle(person.group.rotation.y, person.faceYaw, 12, dt);
    if (t >= 1) {
      person.group.removeFromParent();
      car.visual.add(person.group);
      person.group.position.set(seat.x, seat.y, seat.z);
      person.group.rotation.set(0, Math.PI, 0);
      person.body.geometry = person.seatedGeometry;
      person.state = 'rakshaRiding';
      person.rideDistance = 0;
    }
  }

  #startRakshaDropOff(car) {
    let nearestSample = this.route.samples[0];
    let nearestDistance = Infinity;
    for (const sample of this.route.samples) {
      const distance = (sample.position.x - car.x) ** 2 + (sample.position.z - car.z) ** 2;
      if (distance < nearestDistance) {
        nearestDistance = distance;
        nearestSample = sample;
      }
    }
    const dx = car.x - nearestSample.position.x;
    const dz = car.z - nearestSample.position.z;
    const lateral = dx * nearestSample.right.x + dz * nearestSample.right.z;
    const side = lateral >= 0 ? 1 : -1;
    const outwardX = nearestSample.right.x * side;
    const outwardZ = nearestSample.right.z * side;
    const forwardX = -Math.sin(car.yaw);
    const forwardZ = -Math.cos(car.yaw);
    const seats = rideSeats(car);
    const door = rideDoorPoint(car);
    const distanceLeft = car.exitLength - car.exitDistance;
    // A full bus does not empty at every stop: only the passengers who have
    // ridden far enough get off, and everyone else stays aboard.
    const remaining = [];

    for (const person of car.raksha.passengers) {
      const eligible = person.state === 'rakshaRiding' && (
        person.rideDistance >= car.rakshaConfig.minRideDistance || distanceLeft < 35
      );
      if (!eligible) {
        remaining.push(person);
        continue;
      }
      this.level.root.attach(person.group);
      person.body.geometry = person.standingGeometry;
      person.dropStart.copy(person.group.position);
      person.dropTime = 0;
      person.faceYaw = Math.atan2(outwardX, outwardZ);
      // A raksha drops a fare beside their seat in the open tub; a bus sets
      // everyone down at the door they came in through.
      const dropZ = car.rideType === 'bus'
        ? door.z
        : (seats[person.slot] ?? seats[0]).z;
      person.dropTarget.set(
        car.x + outwardX * 1.8 + forwardX * dropZ,
        SIDEWALK_Y,
        car.z + outwardZ * 1.8 + forwardZ * dropZ,
      );
      person.leaveTarget.copy(person.dropTarget);
      person.leaveTarget.x += outwardX * 3.2;
      person.leaveTarget.z += outwardZ * 3.2;
      person.state = 'rakshaDropping';
      person.respawnDelay = car.rakshaConfig.respawnDelay;
      person.slot = -1;
      person.rideDistance = 0;
    }
    car.raksha.passengers = remaining;
  }

  #updateRakshaDropping(person, dt) {
    person.dropTime += dt / 0.5;
    const t = clamp(person.dropTime, 0, 1);
    const eased = t * t * (3 - 2 * t);
    person.group.position.lerpVectors(person.dropStart, person.dropTarget, eased);
    person.group.position.y += Math.sin(Math.PI * t) * 0.3;
    person.group.rotation.y = dampAngle(person.group.rotation.y, person.faceYaw, 12, dt);
    if (t >= 1) {
      person.group.position.copy(person.dropTarget);
      person.state = 'rakshaLeaving';
      person.walkPhase = 0;
    }
  }

  #updateRakshaLeaving(person, dt) {
    const dx = person.leaveTarget.x - person.group.position.x;
    const dz = person.leaveTarget.z - person.group.position.z;
    const distance = Math.hypot(dx, dz);
    if (distance < 0.15) {
      person.group.position.copy(person.leaveTarget);
      person.state = 'delivered';
      person.deliveredBy = 'raksha';
      person.respawnTimer = person.respawnDelay ?? this.rakshaConfig.respawnDelay;
      person.raksha = null;
      return;
    }
    const step = Math.min(distance, 2.1 * dt);
    person.group.position.x += (dx / distance) * step;
    person.group.position.z += (dz / distance) * step;
    person.group.position.y = SIDEWALK_Y + Math.abs(Math.sin(person.walkPhase)) * 0.03;
    person.walkPhase += dt * 8.5;
    person.group.rotation.y = dampAngle(person.group.rotation.y, Math.atan2(dx, dz), 12, dt);
  }

  #updateDelivered(person, dt) {
    if (person.deliveredBy !== 'raksha') return;
    const delay = person.respawnDelay ?? this.rakshaConfig.respawnDelay;
    person.respawnTimer -= dt;
    if (person.respawnTimer < delay - 3) person.group.visible = false;
    if (person.respawnTimer > 0) return;
    person.group.visible = true;
    person.group.position.copy(person.home);
    person.group.rotation.set(0, person.homeYaw, 0);
    person.group.scale.copy(person.restScale);
    person.state = 'waiting';
    person.deliveredBy = null;
    person.slot = -1;
    person.rideDistance = 0;
    person.waving = false;
  }

  #tryPickup() {
    const capacity = Math.min(this.config.capacity, BED_SLOTS.length);
    if (this.#activeRiders().length >= capacity) return;

    const slot = this.#firstFreeSlot();
    if (slot < 0) return;

    let nearest = null;
    let nearestDistance = this.config.pickupRadius;

    for (const person of this.people) {
      if (person.state !== 'waiting') continue;
      const distance = person.truckDistance;
      if (distance <= nearestDistance) {
        nearest = person;
        nearestDistance = distance;
      }
    }

    if (!nearest) return;

    nearest.state = 'boarding';
    nearest.slot = slot;
    nearest.rideDistance = 0;
    nearest.boardLeg = 0;
    nearest.waving = false;
  }

  #tryDropOff() {
    const eligible = this.people.filter(
      (person) =>
        person.state === 'riding' &&
        person.rideDistance >= this.config.minRideDistance,
    );
    if (!eligible.length) return;

    this.#startDropOff(eligible);
  }

  #startDropOff(riders) {
    this.#truckRight(this._right);
    this.#truckForward(this._forward);
    const tx = this.vehicle.mesh.position.x;
    const tz = this.vehicle.mesh.position.z;
    let count = 0;

    for (const person of riders) {
      const slot = person.slot;
      if (slot < 0) continue;

      // Preserve the world transform while moving back into the level scene.
      this.level.root.attach(person.group);
      person.body.geometry = person.standingGeometry;
      person.dropStart.copy(person.group.position);
      person.dropTime = 0;
      person.faceYaw = Math.atan2(this._right.x, this._right.z);

      // Step out on the truck's right-hand side, alongside the bed.
      person.dropTarget
        .copy(this.vehicle.mesh.position)
        .addScaledVector(this._right, 1.8 + BED_SLOTS[slot].x)
        .addScaledVector(this._forward, BED_SLOTS[slot].z + 0.1);
      person.dropTarget.y = SIDEWALK_Y;

      // Then walk clear of the street, onto the kerb on this hitchhiker's side.
      // Using the home lateral keeps them off the lane no matter which way the
      // truck is facing — the old code used the truck's right vector, which
      // could point into the road and leave them standing in the carriageway.
      let lx = person.home.x - tx;
      let lz = person.home.z - tz;
      const along = lx * this._forward.x + lz * this._forward.z;
      lx -= this._forward.x * along;
      lz -= this._forward.z * along;
      const llen = Math.hypot(lx, lz) || 1;
      person.leaveTarget.copy(person.dropTarget);
      person.leaveTarget.x += (lx / llen) * KERB_DISTANCE;
      person.leaveTarget.z += (lz / llen) * KERB_DISTANCE;
      person.leaveTarget.y = SIDEWALK_Y;

      person.state = 'dropping';
      person.slot = -1;
      person.rideDistance = 0;
      count += 1;
    }

    if (count > 0) {
      this.onDropoff?.({
        count,
        money: count * this.config.money,
      });
    }
  }

  #attachToBed(person) {
    person.group.removeFromParent();
    this.vehicle.mesh.add(person.group);
    person.group.position.copy(BED_SLOTS[person.slot]);
    person.group.rotation.set(0, 0, 0);
    person.body.geometry = person.seatedGeometry;

    person.state = 'riding';
    person.rideDistance = 0;
    person.waving = false;
    this.onPickup?.({ person });
  }

  /**
   * Create one kinematic capsule collider per hitchhiker. The capsule is
   * kinematic (treated as infinite mass) so the dynamic player vehicle is
   * stopped on contact instead of the person being knocked aside. Created
   * lazily so a missing or not-yet-ready physics world can never break the
   * level — the people simply remain visual-only until it initialises.
   */
  #ensureColliders() {
    if (this._collidersReady) return;
    const physics = this.level?.world?.physics;
    if (!physics || !physics.ready) return;

    for (const person of this.people) {
      person.collider = physics.addBody({
        shape: { type: 'capsule', radius: person.colliderRadius, height: 0.9 * person.restScale.y },
        mass: 0,
        kinematic: true,
        position: [
          person.group.position.x,
          person.group.position.y + person.colliderCenter,
          person.group.position.z,
        ],
        group: GROUP.PERSON,
        mask: MASK.PERSON,
        friction: 0,
        restitution: 0,
      });
      person._colliderOn = false;
    }
    this._collidersReady = true;
  }

  /**
   * A person only needs a live collider while it is a free agent on the ground.
   * While boarding / climbing / riding / dropping it lives inside or beside the
   * truck, where a solid body would fight the vehicle it is entering (or shove a
   * stopped truck). In those states the collider is parked out of the world.
   */
  #colliderActive(state) {
    return (
      state === 'waiting' ||
      state === 'returning' ||
      state === 'leaving' ||
      state === 'delivered' ||
      state === 'rakshaLeaving'
    );
  }

  #updateCollider(person) {
    const body = person.collider;
    if (!body) return;

    if (this.#colliderActive(person.state)) {
      person.group.getWorldPosition(this._colliderPos);
      this._colliderPos.y += person.colliderCenter;
      body.teleport(this._colliderPos, QUAT_IDENTITY);
      person._colliderOn = true;
    } else if (person._colliderOn) {
      body.teleport(COLLIDER_PARKED, QUAT_IDENTITY);
      person._colliderOn = false;
    }
  }

  #activeRiders() {
    return this.people.filter(
      (person) =>
        person.state === 'boarding' ||
        person.state === 'climbing' ||
        person.state === 'riding',
    );
  }

  #firstFreeSlot() {
    const capacity = Math.min(this.config.capacity, BED_SLOTS.length);
    for (let slot = 0; slot < capacity; slot += 1) {
      if (
        !this.people.some(
          (person) =>
            person.slot === slot &&
            (person.state === 'boarding' ||
              person.state === 'climbing' ||
              person.state === 'riding'),
        )
      ) {
        return slot;
      }
    }
    return -1;
  }

  #boardPoint() {
    this.#truckForward(this._forward);
    return this._boardPoint.set(
      this.vehicle.mesh.position.x - this._forward.x * 2.95,
      SIDEWALK_Y,
      this.vehicle.mesh.position.z - this._forward.z * 2.95,
    );
  }

  /**
   * A point just behind the truck and off to the hitchhiker's side, clear of
   * the body. The boarder walks here first (leg 0) so they round the rear
   * instead of cutting through the cab/bed to reach the tailgate.
   */
  #rearSidePoint(person) {
    this.#truckForward(this._forward);
    const tx = this.vehicle.mesh.position.x;
    const tz = this.vehicle.mesh.position.z;

    // Lateral direction from the truck to the hitchhiker's kerb, with the
    // forward component removed so it stays perpendicular to travel.
    let lx = person.home.x - tx;
    let lz = person.home.z - tz;
    const along = lx * this._forward.x + lz * this._forward.z;
    lx -= this._forward.x * along;
    lz -= this._forward.z * along;
    const llen = Math.hypot(lx, lz) || 1;
    lx /= llen;
    lz /= llen;

    const REAR = 2.7; // behind the truck centre
    const CLEAR = 2.1; // outside the body (half-width ~1) on the kerb side
    return this._boardPoint.set(
      tx - this._forward.x * REAR + lx * CLEAR,
      SIDEWALK_Y,
      tz - this._forward.z * REAR + lz * CLEAR,
    );
  }

  #bedWorld(slot, target) {
    target.copy(BED_SLOTS[slot]);
    return this.vehicle.mesh.localToWorld(target);
  }

  #truckForward(target) {
    target.set(0, 0, 1).applyQuaternion(this.vehicle.mesh.quaternion);
    target.y = 0;
    return target.normalize();
  }

  #truckRight(target) {
    target.set(1, 0, 0).applyQuaternion(this.vehicle.mesh.quaternion);
    target.y = 0;
    return target.normalize();
  }

  #updateStatus() {
    const dropping = this.people.filter(
      (person) => person.state === 'dropping',
    );
    const boarding = this.people.some(
      (person) =>
        person.state === 'boarding' || person.state === 'climbing',
    );
    const riders = this.people.filter(
      (person) => person.state === 'riding',
    );

    if (dropping.length > 0) {
      this.status = `Dropping off · +${dropping.length * this.config.money} SDG`;
      this.statusMode = 'dropoff';
      return;
    }

    if (boarding) {
      this.status = 'Picking up hitchhiker…';
      this.statusMode = 'hail';
      return;
    }

    if (riders.length > 0) {
      const eligible = riders.filter(
        (person) => person.rideDistance >= this.config.minRideDistance,
      );

      if (eligible.length > 0) {
        this.status = `Stop to drop off · +${eligible.length * this.config.money} SDG`;
        this.statusMode = 'dropoff';
        return;
      }

      const remaining = Math.min(
        ...riders.map(
          (person) => this.config.minRideDistance - person.rideDistance,
        ),
      );
      // A five-metre step keeps the DOM writes infrequent while the number
      // still visibly counts down as the player drives.
      const shown = Math.max(5, Math.ceil(remaining / 5) * 5);
      const noun = riders.length === 1 ? 'rider' : 'riders';
      this.status = `${riders.length} ${noun} aboard · ${shown} m to drop-off`;
      this.statusMode = 'aboard';
      return;
    }

    const nearby = this.people
      .filter(
        (person) =>
          person.state === 'waiting' &&
          person.truckDistance <= this.config.waveRadius,
      )
      .sort((a, b) => a.truckDistance - b.truckDistance)[0];

    if (nearby) {
      if (nearby.truckDistance <= this.config.pickupRadius) {
        this.status = `Stop to pick up · +${this.config.money} SDG`;
      } else if (this.speed < this.config.stopSpeed) {
        this.status = 'Hitchhiker nearby · pull closer';
      } else {
        this.status = 'Hitchhiker ahead · slow down';
      }
      this.statusMode = 'hail';
      return;
    }

    this.status = '';
    this.statusMode = 'hail';
  }
}

/* ------------------------------------------------------------------ */
/* Geometry                                                            */
/* ------------------------------------------------------------------ */

function createFigure(look, k, material, index) {
  const group = new Group();
  const standingGeometry = buildBodyGeometry(look, k, index);
  const seatedGeometry = buildSeatedBodyGeometry(look, k, index);

  const body = new Mesh(standingGeometry, material);
  body.castShadow = true;
  body.receiveShadow = true;
  body.name = 'hitchhiker-body';
  body.rotation.x = k.stoop;
  group.add(body);

  const skin = SKIN_TONES[index % SKIN_TONES.length];
  const sleeve = k.clothing === 'youth'
    ? (look.shirt ?? YOUTH_SHIRTS[index % YOUTH_SHIRTS.length])
    : look.robe;
  const armGeometry = buildArmGeometry(k, skin, sleeve);

  const leftArm = new Group();
  leftArm.position.set(-0.245, 1.18, 0.01);
  leftArm.rotation.z = -0.08;
  const leftMesh = new Mesh(armGeometry, material);
  leftMesh.castShadow = true;
  leftMesh.name = 'hitchhiker-arm-left';
  leftArm.add(leftMesh);
  group.add(leftArm);

  const rightArm = new Group();
  rightArm.position.set(0.245, 1.18, 0.01);
  rightArm.rotation.z = 0.08;
  const rightMesh = new Mesh(armGeometry, material);
  rightMesh.castShadow = true;
  rightMesh.name = 'hitchhiker-arm-right';
  rightArm.add(rightMesh);
  group.add(rightArm);

  return { group, leftArm, rightArm, body, standingGeometry, seatedGeometry, stoop: k.stoop };
}

function buildBodyGeometry(look, k, index) {
  const parts = new FigureParts();
  const skin = SKIN_TONES[index % SKIN_TONES.length];
  const r = k.torsoR;

  if (k.clothing === 'youth') {
    const shirt = look.shirt ?? YOUTH_SHIRTS[index % YOUTH_SHIRTS.length];
    // Trousers: two legs from the hip down to the ankle.
    for (const x of [-0.1, 0.1]) {
      parts.limb(x, 0.5, 0.0, x, 0.06, 0.04, 0.082, TROUSERS);
      parts.box(0.13, 0.05, 0.24, SHOE, x, 0.03, 0.06);
    }
    // A short t-shirt instead of a floor-length robe.
    parts.cyl(r * 0.92, r * 1.14, 0.5, 8, shirt, 0, 0.7, 0);
    parts.cyl(r * 0.9, r * 1.02, 0.34, 8, shirt, 0, 1.07, 0);
  } else {
    // A long robe / abaya. The abaya flares a touch wider at the hem.
    const hemR = k.clothing === 'abaya' ? 0.31 : 0.29;
    parts.cyl(0.2, hemR, 1.12, 8, look.robe, 0, 0.68, 0);
    parts.cyl(0.2, 0.21, 0.1, 8, look.wrap, 0, 1.23, 0);
  }

  addUpperBody(parts, look, k, skin, index);

  return parts.merge();
}

/**
 * The neck, head and head-covering shared by every body type and pose. Women
 * get a headscarf (hijab) that drapes to the shoulders; men and elders get a
 * cap; teenagers get a bare head with a short crop of hair.
 */
function addUpperBody(parts, look, k, skin, index) {
  parts.cyl(0.055, 0.06, 0.08, 6, skin, 0, 1.29, 0);
  parts.sphere(0.13, 8, 6, skin, 0, 1.42, 0);
  parts.sphere(0.026, 5, 4, SKIN_DARK, 0, 1.41, 0.125);

  if (k.headCover === 'hijab') {
    const hc = HIJAB_TONES[index % HIJAB_TONES.length];
    // The scarf covers the hair and falls past the shoulders.
    parts.sphere(0.155, 8, 6, hc, 0, 1.45, 0);
    parts.cyl(0.16, 0.19, 0.22, 8, hc, 0, 1.3, 0);
  } else if (k.headCover === 'hat' || k.headCover === 'hat-grey') {
    const hc = k.headCover === 'hat-grey' ? 0xb9b3a6 : look.wrap;
    parts.cyl(0.128, 0.145, 0.09, 8, hc, 0, 1.5, 0);
    parts.cyl(0.135, 0.1, 0.035, 8, hc, 0, 1.55, 0);
  } else {
    const hair = HAIR_TONES[index % HAIR_TONES.length];
    parts.sphere(0.138, 8, 6, hair, 0, 1.45, 0);
  }
}

/**
 * The same person, sitting. The hips sit at local y ≈ 0.5 and the legs fold
 * forward to the feet, which stay at the group origin so the seated figure
 * occupies the same floor patch as the standing one — only the knees come up.
 * The torso, shoulders and head are identical to the standing body, so swapping
 * the mesh in while riding reads as the person dropping onto the seat rather
 * than changing shape.
 */
function buildSeatedBodyGeometry(look, k, index) {
  const parts = new FigureParts();
  const skin = SKIN_TONES[index % SKIN_TONES.length];
  const r = k.torsoR;
  const cloth = k.clothing === 'youth' ? TROUSERS : look.robe;

  // Thigh forward-and-down to the knee, then shin down to the ankle.
  for (const x of [-0.12, 0.12]) {
    parts.limb(x, 0.5, 0.02, x * 1.3, 0.42, 0.42, 0.1, cloth);
    parts.limb(x * 1.3, 0.42, 0.42, x * 1.1, 0.05, 0.52, 0.09, cloth);
  }
  // Seat pad / folded hem around the hips.
  parts.cyl(0.21, 0.24, 0.2, 8, cloth, 0, 0.46, 0.08);

  // Feet tucked under the knees.
  for (const x of [-0.13, 0.13]) {
    parts.box(0.13, 0.055, 0.24, SHOE, x, 0.03, 0.56);
  }

  // Upper body connecting the seat to the shoulders.
  if (k.clothing === 'youth') {
    const shirt = look.shirt ?? YOUTH_SHIRTS[index % YOUTH_SHIRTS.length];
    parts.cyl(r * 0.92, r * 1.14, 0.4, 8, shirt, 0, 0.76, 0);
    parts.cyl(r * 0.9, r * 1.02, 0.34, 8, shirt, 0, 1.07, 0);
  } else {
    // One continuous robe from the hips up to the collar so there is no gap
    // across the chest when seated.
    parts.cyl(0.21, 0.24, 0.62, 8, look.robe, 0, 0.87, 0);
    parts.cyl(0.2, 0.21, 0.1, 8, look.wrap, 0, 1.23, 0);
  }

  addUpperBody(parts, look, k, skin, index);

  return parts.merge();
}

function buildArmGeometry(k, skin, sleeve) {
  const parts = new FigureParts();
  const ar = 0.05 * k.bScale;
  parts.cyl(ar, ar * 1.12, 0.36, 6, sleeve, 0, -0.18, 0);
  parts.cyl(ar * 0.84, ar * 0.94, 0.25, 6, sleeve, 0, -0.47, 0);
  parts.sphere(ar * 1.1, 6, 5, skin, 0, -0.61, 0);
  return parts.merge();
}

/**
 * Collects primitives with a flat colour each and bakes them into one
 * vertex-coloured geometry. This mirrors the city/tea-stand geometry style.
 */
class FigureParts {
  constructor() {
    this.geometries = [];
    this.stack = [new Matrix4()];
  }

  get matrix() {
    return this.stack[this.stack.length - 1];
  }

  box(width, height, depth, color, x, y, z) {
    const geometry = new BoxGeometry(width, height, depth);
    geometry.translate(x, y, z);
    this.#push(geometry, color);
  }

  cyl(radiusTop, radiusBottom, height, segments, color, x, y, z) {
    const geometry = new CylinderGeometry(
      radiusTop,
      radiusBottom,
      height,
      segments,
    );
    geometry.translate(x, y, z);
    this.#push(geometry, color);
  }

  sphere(radius, widthSegments, heightSegments, color, x, y, z) {
    const geometry = new SphereGeometry(radius, widthSegments, heightSegments);
    geometry.translate(x, y, z);
    this.#push(geometry, color);
  }

  /**
   * A limb segment drawn as a cylinder spanning two points — used for the bent
   * knees of the seated body. The cylinder is built along +Y then rotated to
   * point from `from` to `to`.
   */
  limb(ax, ay, az, bx, by, bz, radius, color) {
    const from = new Vector3(ax, ay, az);
    const to = new Vector3(bx, by, bz);
    const direction = new Vector3().subVectors(to, from);
    const length = direction.length();
    if (length < 1e-4) return;
    const geometry = new CylinderGeometry(radius, radius, length, 7);
    const midpoint = new Vector3().addVectors(from, to).multiplyScalar(0.5);
    const quaternion = new Quaternion().setFromUnitVectors(
      new Vector3(0, 1, 0),
      direction.normalize(),
    );
    geometry.applyQuaternion(quaternion);
    geometry.translate(midpoint.x, midpoint.y, midpoint.z);
    this.#push(geometry, color);
  }

  merge() {
    if (!this.geometries.length) return null;
    const merged = mergeGeometries(this.geometries, false);
    for (const geometry of this.geometries) geometry.dispose();
    this.geometries.length = 0;
    return merged;
  }

  #push(geometry, color) {
    geometry.applyMatrix4(this.matrix);

    const flat = geometry.index ? geometry.toNonIndexed() : geometry;
    if (flat !== geometry) geometry.dispose();

    const count = flat.attributes.position.count;
    const colors = new Float32Array(count * 3);
    const parsed = new Color(color);
    for (let i = 0; i < count; i += 1) {
      colors[i * 3] = parsed.r;
      colors[i * 3 + 1] = parsed.g;
      colors[i * 3 + 2] = parsed.b;
    }
    flat.setAttribute('color', new Float32BufferAttribute(colors, 3));

    this.geometries.push(flat);
  }
}
